import { useEffect, useState } from "react";
import { Activity, Building2, Crown, DollarSign, Users } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Kpi, PageHeader, Section, Spinner } from "@/components/kit";
import { api, brl, dateBR, errMsg, KIND_LABEL } from "@/lib/api";

const T = ({ head, rows, testid }) => (
  <div className="overflow-x-auto">
    <table className="w-full min-w-[720px] text-sm" data-testid={testid}>
      <thead className="text-left text-xs uppercase text-slate-400"><tr>{head.map((h) => <th key={h} className="px-2 py-2">{h}</th>)}</tr></thead>
      <tbody>{rows.map((r, i) => <tr key={i} className="border-t border-[#1E3A6E]/50">{r.map((c, j) => <td key={j} className="px-2 py-2.5">{c}</td>)}</tr>)}</tbody>
    </table>
  </div>
);

function UsersTab() {
  const [q, setQ] = useState("");
  const [rows, setRows] = useState(null);
  const load = () => api.get("/admin/users", { params: { q } }).then((r) => setRows(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const patch = async (id, body) => { try { await api.patch(`/admin/users/${id}`, body); toast.success("Usuário atualizado"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!rows) return <Spinner />;
  return (
    <Section>
      <form onSubmit={(e) => { e.preventDefault(); load(); }} className="mb-4 flex gap-2"><Input placeholder="Buscar nome ou e-mail" value={q} onChange={(e) => setQ(e.target.value)} className="max-w-xs bg-[#0B1A33]" data-testid="admin-user-search" /><Button type="submit" data-testid="admin-user-search-btn">Buscar</Button></form>
      <T testid="admin-users-table" head={["Usuário", "Papel", "Plano", "Espaços", "Criado", "Ações"]} rows={rows.map((u) => [
        <div key="u"><div>{u.name}</div><div className="text-xs text-slate-500">{u.email}</div></div>, u.role, `${u.plan} (${u.plan_status})`, u.workspaces, dateBR(u.created_at),
        <div key="a" className="flex flex-wrap gap-1">
          <select defaultValue="" onChange={(e) => e.target.value && patch(u.id, { grant_plan: e.target.value })} className="h-8 rounded border border-[#1E3A6E] bg-[#0B1A33] px-2 text-xs" data-testid={`admin-grant-${u.id}`}>
            <option value="">Conceder plano…</option>{["essencial", "familia", "empresarial", "premium", "none"].map((p) => <option key={p} value={p}>{p === "none" ? "Remover plano" : p}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-8 border-[#1E3A6E] bg-transparent text-xs" onClick={() => patch(u.id, { disabled: !u.disabled })} data-testid={`admin-toggle-disable-${u.id}`}>{u.disabled ? "Desbloquear" : "Bloquear"}</Button>
          <Button size="sm" variant="outline" className="h-8 border-[#1E3A6E] bg-transparent text-xs" onClick={() => patch(u.id, { role: u.role === "admin" ? "user" : "admin" })} data-testid={`admin-toggle-role-${u.id}`}>{u.role === "admin" ? "Tornar usuário" : "Tornar admin"}</Button>
        </div>])} />
    </Section>
  );
}

function PlansTab() {
  const [plans, setPlans] = useState(null);
  const load = () => api.get("/billing/plans").then((r) => setPlans(r.data.plans));
  useEffect(() => { load(); }, []);
  const save = async (p) => { try { await api.put(`/admin/plans/${p.key}`, { price: Number(p.price), ai_month: Number(p.ai_month), max_workspaces: Number(p.max_workspaces) }); toast.success("Plano salvo"); load(); } catch (e) { toast.error(errMsg(e)); } };
  if (!plans) return <Spinner />;
  return (
    <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
      {plans.map((p, i) => (
        <Section key={p.key} title={p.name} testid={`admin-plan-${p.key}`}>
          {[["price", "Preço (R$)"], ["ai_month", "Mensagens IA/mês"], ["max_workspaces", "Máx. espaços"]].map(([k, l]) => (
            <label key={k} className="mb-2 block text-xs text-slate-400">{l}<Input type="number" step="any" value={p[k]} onChange={(e) => setPlans(plans.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} className="mt-1 bg-[#0B1A33]" data-testid={`admin-plan-${p.key}-${k}`} /></label>
          ))}
          <Button size="sm" className="mt-2 w-full" onClick={() => save(p)} data-testid={`admin-plan-${p.key}-save`}>Salvar</Button>
        </Section>
      ))}
    </div>
  );
}

function Lazy({ path, render }) {
  const [d, setD] = useState(null);
  useEffect(() => { api.get(path).then((r) => setD(r.data)); }, [path]);
  return d ? render(d) : <Spinner />;
}

export default function Admin() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then((r) => setS(r.data)); }, []);
  if (!s) return <Spinner />;
  return (
    <div data-testid="admin-page">
      <PageHeader eyebrow="SaaS" title="Painel administrativo" subtitle="Usuários, espaços (empresas e famílias), assinaturas, receita recorrente e auditoria." />
      <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Usuários" value={s.users} icon={Users} hint={`${s.new_users_30d} novos em 30 dias · ${s.trial_users} em teste`} testid="admin-kpi-users" />
        <Kpi label="MRR" value={brl(s.mrr)} tone="gold" icon={DollarSign} hint={`Receita aprovada total ${brl(s.revenue_approved)}`} testid="admin-kpi-mrr" />
        <Kpi label="Assinaturas ativas" value={s.active_subscriptions} tone="green" icon={Crown} hint={Object.entries(s.subscriptions_by_plan).map(([k, v]) => `${k}: ${v}`).join(" · ")} testid="admin-kpi-subs" />
        <Kpi label="Espaços" value={s.workspaces} icon={Building2} hint={`Churn 30d ${s.churn_pct}% · IA ${s.ai_messages_month} msgs/mês`} testid="admin-kpi-workspaces" />
      </div>
      <Tabs defaultValue="users">
        <TabsList className="mb-6 flex h-auto flex-wrap bg-[#0B1A33]">
          {[["users", "Usuários"], ["ws", "Espaços / Empresas"], ["subs", "Assinaturas"], ["plans", "Planos"], ["audit", "Auditoria"]].map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`admin-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        <TabsContent value="users"><UsersTab /></TabsContent>
        <TabsContent value="ws"><Section><Lazy path="/admin/workspaces" render={(rows) => <T testid="admin-ws-table" head={["Espaço", "Tipo", "Proprietário", "Membros", "Lançamentos", "Criado"]}
          rows={rows.map((w) => [w.name, KIND_LABEL[w.kind], w.owner_email, w.members, w.transactions, dateBR(w.created_at)])} />} />
          <p className="mt-3 text-xs text-slate-500">Por minimização de dados (LGPD), o painel não exibe valores financeiros dos clientes.</p></Section></TabsContent>
        <TabsContent value="subs"><Section><Lazy path="/admin/subscriptions" render={(d) => <T testid="admin-subs-table" head={["Usuário", "Plano", "Valor", "Status", "Modo", "Criada"]}
          rows={d.subscriptions.map((x) => [x.email, x.plan_name, brl(x.amount), x.status, x.mode, dateBR(x.created_at)])} />} /></Section></TabsContent>
        <TabsContent value="plans"><PlansTab /></TabsContent>
        <TabsContent value="audit"><Section><Lazy path="/admin/audit" render={(rows) => <T testid="admin-audit-table" head={["Data", "Ação", "Usuário", "IP"]}
          rows={rows.map((a) => [new Date(a.at).toLocaleString("pt-BR"), <span key="a" className="inline-flex items-center gap-1"><Activity className="h-3 w-3 text-[#D4AF37]" />{a.action}</span>, a.user_id?.slice(0, 8), a.ip || "—"])} />} /></Section></TabsContent>
      </Tabs>
    </div>
  );
}
